import {
  Box,
  MenuButton as ChakraMenuButton,
  FormControl,
  FormErrorMessage,
  FormLabel,
  InputGroup,
  InputLeftElement,
  type InputProps,
  InputRightElement,
  Portal,
  Text,
  forwardRef,
} from '@chakra-ui/react'
import styled from '@emotion/styled'
import type { ReactNode } from 'react'
import CurrencyInput, { type CurrencyInputProps } from 'react-currency-input-field'
import { ChevronDown } from 'react-feather'
import { BaseTooltip } from './Tooltip'
import { FlexRow } from './flex'
import { Menu, MenuItem, MenuList } from './ui/Menu'

interface IProps {
  label: string
  labelFontSize?: string
  labelFontColor?: string
  errorMessage?: string
  showInfo?: boolean
  helperText?: string
  subtitle?: string
  IconRight?: ReactNode
  appendOutside?: string
  labelRight?: string
  /** Turns `labelRight` into a dropdown with these options, styled like the static label */
  labelRightOptions?: readonly string[]
  onLabelRightChange?: (value: string) => void
  labelLeft?: string
  classname?: string
  decimalsLimit?: number
  // TODO: Once all currency inputs have been replaced with new styles, remove this prop.
  /** Whether to apply new currency input styles or not.*/
  newStyle?: boolean
  allowNegativeValue?: boolean
  onValueChange?: (value, name, values) => void
  prependComponent?: ReactNode
  formControlWidth?: string
  componentStyle?: React.CSSProperties
  focusBorderColor?: string
}

const commonStyles = `
  padding: 3px 0 0 20px;
  padding-right: 10px;
  vertical-align: center;
  width: 100%;
  transition: border 200ms ease-in-out;
`

const NewStyleCurrencyInput = styled(CurrencyInput)<CurrencyInputProps & { focusBorderColor?: string }>`
  ${commonStyles}
  border: ${props => (props.disabled ? '1px solid var(--color-border)' : '1px solid var(--color-border)')};
  border-radius: 8px;
  height: 32px;
  color: ${props => (props.disabled ? '#656565' : '#0D0D0D')};
  background-color: ${props => (props.disabled ? 'transparent' : 'var(--color-background)')};
  text-align: right;
  font-size: 14px;
  &:hover {
    border-color: ${props => (props.disabled ? 'var(--color-border)' : 'var(--color-border-hover)')};
  }
  &:focus {
    outline: none;
    border: 1px solid ${props => props.focusBorderColor || 'var(--color-primary)'};
  };
`

const OldStyleCurrencyInput = styled(CurrencyInput)<CurrencyInputProps>`
  ${commonStyles}
  border: 2px solid #b4b4b4;
  border-radius: 8px;
  height: 48px;
  color: #0D0D0D;
  text-align: left;
  &:hover {
    border-color: #e2e8f0;
  }
  &:focus {
    outline: none;
    border: 2px solid #68d391;
  }
`

export const NumericInput = forwardRef<InputProps & IProps, 'input'>(
  (
    {
      classname,
      label,
      labelFontSize,
      errorMessage,
      showInfo = false,
      helperText,
      subtitle,
      IconRight,
      labelRight,
      labelRightOptions,
      onLabelRightChange,
      labelLeft,
      appendOutside,
      newStyle,
      labelFontColor,
      width,
      minWidth,
      textAlign,
      decimalsLimit = 4,
      allowNegativeValue,
      isRequired,
      onValueChange,
      prependComponent,
      formControlWidth,
      componentStyle,
      focusBorderColor,
      ...props
    },
    ref
  ) => {
    const inputProps = {
      style: {
        width: width,
        minWidth: minWidth,
        textAlign: textAlign,
        // Keeps the typed value from running under the unit dropdown
        paddingRight: labelRightOptions ? '64px' : undefined,
        // The global input CSS only adds the bottom padding once a value is typed, which pushes the placeholder off-center next to the unit
        paddingBottom: labelRightOptions ? '3px' : undefined,
        ...componentStyle,
      },
      className: classname ? classname : 'currency-input',
      placeholder: '',
      ...(props as any),
      ref: ref,
      decimalsLimit,
      intlConfig: { locale: 'de-DE' },
      focusBorderColor,
      onValueChange: (value, name, values) => {
        if (values?.formatted.includes('NaN')) return
        onValueChange?.(value, name, values)
      },
    }

    const sideLabelStyle = { fontWeight: 400, fontSize: newStyle ? '14px' : '16px', color: '#656565' }

    return (
      <FormControl isInvalid={props.isInvalid} isRequired={isRequired} width={formControlWidth ?? undefined}>
        <FlexRow marginBottom={label ? 2 : 0} alignItems='center' paddingRight={'8px'}>
          <FormLabel
            color={labelFontColor ? labelFontColor : undefined}
            fontSize={labelFontSize ? labelFontSize : undefined}
            marginBottom={0}
            whiteSpace={'nowrap'}
          >
            {label}
          </FormLabel>
          {showInfo && <BaseTooltip blackIcon={newStyle} width={'16px'} height={'16px'} label={helperText} />}
        </FlexRow>
        {subtitle && (
          <Text fontSize={'14px'} color={'#848484'} fontFamily={'Parabole'} marginTop={'-14px'} marginBottom={'8px'}>
            {subtitle}
          </Text>
        )}

        <FlexRow alignItems={'center'} gap={3}>
          {prependComponent && prependComponent}
          <InputGroup w='full'>
            {newStyle ? <NewStyleCurrencyInput {...inputProps} /> : <OldStyleCurrencyInput {...inputProps} />}
            {appendOutside && appendOutside.length > 0 && <Box marginLeft={'8px'}>{appendOutside}</Box>}
            {IconRight && (
              <InputRightElement pointerEvents='none' top='unset' paddingRight={3}>
                {IconRight}
              </InputRightElement>
            )}
            {labelRight && labelRightOptions && (
              <InputRightElement h='full' w='auto' pr='10px'>
                <Menu placement='bottom-end'>
                  <ChakraMenuButton type='button' disabled={props.disabled} bg='transparent' cursor='pointer'>
                    <FlexRow alignItems='center' gap='2px'>
                      <Text {...sideLabelStyle}>{labelRight}</Text>
                      <ChevronDown size={14} color={sideLabelStyle.color} />
                    </FlexRow>
                  </ChakraMenuButton>
                  {/* Portaled so the list isn't painted underneath the fields that follow the input */}
                  <Portal>
                    <MenuList minW='80px' zIndex='popover'>
                      {labelRightOptions.map(option => (
                        <MenuItem
                          key={option}
                          class='standard'
                          isSelected={option === labelRight}
                          onClick={() => onLabelRightChange?.(option)}
                        >
                          {option}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Portal>
                </Menu>
              </InputRightElement>
            )}
            {labelRight && !labelRightOptions && (
              <InputRightElement pointerEvents='none' h='full' w='full'>
                <Text {...sideLabelStyle} w='full' textAlign={'end'} pr='10px'>
                  {labelRight}
                </Text>
              </InputRightElement>
            )}
            {labelLeft && (
              <InputLeftElement pointerEvents='none' h='full' w='full'>
                <Text {...sideLabelStyle} w='full' textAlign={'start'} pl='10px'>
                  {labelLeft}
                </Text>
              </InputLeftElement>
            )}
          </InputGroup>
        </FlexRow>
        {props.isInvalid && errorMessage ? (
          <FormErrorMessage position='absolute' bottom='-6' top='auto' fontSize='12px'>
            {errorMessage}
          </FormErrorMessage>
        ) : null}
      </FormControl>
    )
  }
)
